import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFacade, FacadePoint } from '../lib/hooks/useFacade';
import { useBuildingConfig } from '../lib/hooks/useBuildingConfig';
import { useIsTouch } from '../lib/hooks/useIsTouch';

interface FacadeSectionProps {
  // When false, the outer <section> heading is hidden (e.g. embedded above the viewer).
  showHeading?: boolean;
  className?: string;
}

const pointsToPath = (points: FacadePoint[]): string => {
  if (!points || points.length === 0) return '';
  return `M ${points[0].x} ${points[0].y} ${points
    .slice(1)
    .map((p) => `L ${p.x} ${p.y}`)
    .join(' ')} Z`;
};

// Center of the polygon's bounding box — a more visually centered anchor for
// the label than the average of the (possibly uneven) vertices.
const bboxCenter = (points: FacadePoint[]): FacadePoint => {
  if (!points.length) return { x: 0, y: 0 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
};

// Public interactive building facade: each drawn floor region links to its
// floor plan in the navigator. Renders nothing until an image + areas exist.
const FacadeSection: React.FC<FacadeSectionProps> = ({ showHeading = true, className = '' }) => {
  const { facade, loading } = useFacade();
  const { getEntranceLabel } = useBuildingConfig();
  const navigate = useNavigate();
  const imageRef = useRef<HTMLImageElement>(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // No hover on touch devices — the clickable floor zones must stay visible.
  const isTouch = useIsTouch();

  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      setDims({ width: naturalWidth, height: naturalHeight });
    }
  };

  // Nothing configured yet — stay invisible instead of showing an empty block.
  if (loading || facade.facades.length === 0) {
    return null;
  }

  const active = facade.facades[Math.min(activeIndex, facade.facades.length - 1)];

  const openArea = (entrance: string, floor: number) => {
    navigate(`/navigator/${entrance}/${floor}`);
  };

  return (
    <section className={`py-16 bg-neutral-50 ${className}`}>
      <div className="container-custom">
        {showHeading && (
          <div className="text-center mb-10">
            <h2 className="section-title">Разгледайте сградата</h2>
            <p className="text-neutral-600 max-w-2xl mx-auto -mt-4">
              Изберете етаж от фасадата, за да видите неговия план и свободните апартаменти.
            </p>
          </div>
        )}

        {/* Tabs appear only when there is something to switch between. */}
        {facade.facades.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {facade.facades.map((item, index) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveIndex(index);
                  setDims({ width: 0, height: 0 });
                  setHoveredId(null);
                }}
                className={`px-5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  index === activeIndex
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-primary'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}

        <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white">
          <img
            ref={imageRef}
            key={active.id}
            src={active.imageUrl}
            alt={active.name}
            className="w-full h-auto block"
            onLoad={handleImageLoad}
            draggable={false}
          />
          {dims.width > 0 && (
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox={`0 0 ${dims.width} ${dims.height}`}
              preserveAspectRatio="xMidYMid meet"
            >
              {active.areas.map((area) => {
                const isHovered = hoveredId === area.id;
                const c = bboxCenter(area.points);
                const label = area.label || `Етаж ${area.floor}`;
                return (
                  <g key={area.id}>
                    <path
                      d={pointsToPath(area.points)}
                      style={{
                        // Desktop: invisible until hovered. Touch: subtly
                        // visible at all times, since there is no hover.
                        fill: isHovered
                          ? 'rgb(var(--color-secondary) / 0.45)'
                          : isTouch
                          ? 'rgb(var(--color-secondary) / 0.16)'
                          : 'transparent',
                        stroke: isHovered
                          ? 'rgb(var(--color-secondary))'
                          : isTouch
                          ? 'rgb(var(--color-secondary) / 0.85)'
                          : 'transparent',
                        pointerEvents: 'all',
                      }}
                      strokeWidth={dims.width * 0.0025}
                      className="cursor-pointer transition-all duration-200"
                      onMouseEnter={() => setHoveredId(area.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => openArea(area.entrance, area.floor)}
                    />
                    {(isHovered || isTouch) && (
                      <text
                        x={c.x}
                        y={c.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#ffffff"
                        style={{ paintOrder: 'stroke' }}
                        stroke="rgba(0,0,0,0.6)"
                        strokeWidth={dims.width * 0.004}
                        fontSize={dims.width * 0.022}
                        fontWeight={700}
                        className="pointer-events-none select-none"
                      >
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
        </div>

        {isTouch && (
          <p className="mt-3 text-center text-sm text-neutral-500">
            Докоснете етаж от сградата, за да отворите неговия план
          </p>
        )}
      </div>
    </section>
  );
};

export default FacadeSection;
